<script setup>
import { inject, ref, reactive, computed, watch, onMounted } from "vue";
import Block from "../checkout/modules/Block.vue";
import Account from "@/modules/Account.js";
import moment from "moment";
import {
  RadioGroup,
  RadioGroupDescription,
  RadioGroupLabel,
  RadioGroupOption,
} from "@headlessui/vue";
import useVuelidate from "@vuelidate/core";
import { required, numeric, minLength } from "@vuelidate/validators";
import Auth from "@/modules/Auth.js";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Icon from "../../../components/Icon.vue";
import InvoiceRow from './InvoiceRow.vue'
import ZelleInstructions from "../../../components/payment-instructions/ZelleInstructions.vue";
import WireInstructions from "../../../components/payment-instructions/WireInstructions.vue";
import CheckInstructions from "../../../components/payment-instructions/CheckInstructions.vue";
import Mymixin from "@/modules/mixins";

const currencyFormat = Mymixin.methods.currencyFormat;

const store = useStore();

const axios = inject("axios");

const { isLoggedIn } = Auth();

const { invoices, getInvoices, getCreditCards, isInvoicesLoading } = Account();

getInvoices();

const formatDate = (date) => moment(date).format("MMM Do YYYY");

const payinvoices = ref(false);

const otherinvoices = computed(() => {
  return invoices.value?.allInvoices?.filter((inv) => !inv.is_invoice) ?? [];
});

const selectedInvoices = ref([]);
const downloading = ref(false);

const downloadInvoices = () => {
  downloading.value = true;
  axios.api
    .post(
      "/bulk-invoices",
      {
        self_receiver: true,
        invoice_ids: selectedInvoices.value.map((inv) => inv.id),
        company_id: localStorage.getItem("compnay_tg_id"),
        user: localStorage.getItem("user_id"),
      },
      {
        responseType: "blob",
      }
    )
    .then((res) => {
      if(res.data){
        let msg ="Please check your email to download the " +
          (selectedInvoices.value.length > 1 ? "invoices." : "invoice.");
        store.dispatch("setToast", {
          status: true,
          msg: msg,
          type: "success",
        });
      }
      downloading.value = false;
    })
    .catch((error) => {
      console.error("Error:", error);
      downloading.value = false;
    });
};

const currentInvoice = computed(() =>
  invoices.value?.allInvoices?.find((inv) => inv.is_invoice),
);

const outstandingAmount = computed(
  () =>
    invoices.value.allInvoices?.reduce((acc, inv) => {
      if (!inv.paid) acc += parseFloat(inv.payable_amount);
      return acc;
    }, 0) ?? 0,
);

const overdueAmount = computed(
  () =>
    invoices.value.dueInvoices?.reduce((acc, inv) => {
      if (!inv.paid) acc += parseFloat(inv.payable_amount);
      return acc;
    }, 0) ?? 0,
);

const dueInvoicesCount = computed(
  () => invoices.value.dueInvoices?.filter((inv) => !inv.paid).length ?? 0,
);

const openInvoicesCount = computed(
  () => invoices.value.allInvoices?.filter((inv) => !inv.paid).length ?? 0,
);

// Watch currentInvoice and update payinvoices accordingly
watch(
  currentInvoice,
  (newVal) => {
    payinvoices.value = !newVal;
    if (newVal && !selectedInvoices.value.some((s) => s.id === newVal.id)) {
      selectedInvoices.value.push(newVal);
    }
  },
  { immediate: true },
);

const allUnpaidInvoices = computed(() => {
  const due = invoices.value.dueInvoices || [];
  const open = invoices.value.openInvoicesNotDue || [];
  const current = currentInvoice.value ? [currentInvoice.value] : [];

  const all = [...current, ...due, ...open];
  // Filter unique invoices by ID and ensure they are not paid
  const uniqueInvoices = [];
  const map = new Map();
  for (const item of all) {
    if (!map.has(item.id) && !item.paid) {
      map.set(item.id, true);
      uniqueInvoices.push(item);
    }
  }
  return uniqueInvoices;
});

const isSelected = (inv) => selectedInvoices.value.some((s) => s.id === inv.id);

const toggleInvoice = (inv) => {
  const index = selectedInvoices.value.findIndex((s) => s.id === inv.id);
  if (index > -1) {
    selectedInvoices.value.splice(index, 1);
  } else {
    selectedInvoices.value.push(inv);
  }
};

const allSelected = computed(() => {
  return (
    allUnpaidInvoices.value.length > 0 &&
    allUnpaidInvoices.value.every((inv) => isSelected(inv))
  );
});

const toggleAllInvoices = () => {
  if (allSelected.value) {
    selectedInvoices.value = [];
  } else {
    selectedInvoices.value = [...allUnpaidInvoices.value];
  }
};

const defaultCardDetails = {
  nickname: "",
  cardNumber: "",
  name: "",
  expMonth: "",
  expYear: "",
  cvv: "",
  expiry: "",
};

const cardDetails = reactive({ ...defaultCardDetails });

const resetCardDetails = () => {
  Object.assign(cardDetails, defaultCardDetails);
};

const rules = {
  cardDetails: {
    cardNumber: { required },
    name: { required },
    expMonth: { required, numeric },
    expYear: { required, numeric },
    cvv: { required, numeric, minLength: minLength(0) },
  },
};

const v$ = useVuelidate(rules, { cardDetails });

const expMonths = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "April",
  "05": "May",
  "06": "Jun",
  "07": "July",
  "08": "Aug",
  "09": "Sept",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

onMounted(() => {
  document.body.querySelector("#app").classList.add("enable-sticky");
});

const expYears = computed(() => {
  const years = [];
  const currentYear = new Date().getFullYear();
  let updatedYear = currentYear;

  while (updatedYear < currentYear + 25) {
    years.push(updatedYear);
    updatedYear++;
  }
  // console.log(years);
  return years;
});

const getCardType = computed(() => {
  let number = cardDetails.cardNumber;
  let re = new RegExp("^4");
  if (number.match(re) != null) return "visa";

  re = new RegExp("^(34|37)");
  if (number.match(re) != null) return "amex";

  re = new RegExp("^5[1-5]");
  if (number.match(re) != null) return "mastercard";

  re = new RegExp("^6011");
  if (number.match(re) != null) return "discover";

  re = new RegExp("^9792");
  if (number.match(re) != null) return "troy";

  return "visa"; // default type
});

const getCardMask = computed(() => {
  return getCardType.value === "amex"
    ? "#### ###### #####"
    : "#### #### #### ####";
});
const getCvvMask = computed(() => {
  return getCardType.value === "amex" ? "####" : "###";
});
const getCvvMaskForCard = (cardNum) => {
  return cardNum.startsWith("34") || cardNum.startsWith("37") ? "####" : "###";
};
const isAuthenticated = computed(() => {
  return (
    isLoggedIn() &&
    invoices.value?.company_id == localStorage.getItem("company_id")
  );
});

const selectedCreditCard = ref(null);
const selectedPayment = ref(null);
const selectCreditCard = (card) => {
  if (selectedCreditCard.value && card.id == selectedCreditCard.value.id) {
    selectedCreditCard.value = null;
    selectedPayment.value = null;
    return;
  }
  selectedCreditCard.value = card;

  // Reset the card details in the form
  resetCardDetails();
};

const dateDue = computed(() => {
  return (
    currentInvoice.value &&
    moment(currentInvoice.value.due_at).isSameOrBefore(moment(), "day")
  );
});

const isPaymentValid = computed(() => {
  if (!selectedCreditCard.value) return false;
  if (selectedInvoices.value.length === 0) return false;
  if (
    !isAuthenticated.value &&
    (!cardDetails.cvv || cardDetails.cvv.length < 3)
  )
    return false;
  return true;
});

const totalAmount = computed(() => {
  return selectedInvoices.value
    .reduce((acc, inv) => acc + parseFloat(inv.payable_amount), 0)
    .toFixed(2);
});

const showPaymentsModal = ref(false);
const openPaymentModal = () => {
  showPaymentsModal.value = true;
};

const isSubmit = ref(false);
const errorMessage = ref("");
const addNewPaymentCard = async () => {
  isSubmit.value = true;
  const isValid = await v$.value.$validate();
  if (isValid) {
    if (isAuthenticated.value) {
      let dataToPost = {
        ccname: cardDetails.name,
        nickname: cardDetails.nickname,
        ccnumber: cardDetails.cardNumber,
        cvv: cardDetails.cvv,
        ccexp: cardDetails.expMonth + cardDetails.expYear,
      };
      axios.authApi.post("/customer-vault", dataToPost).then((r) => {
        if (r.data.status) {
          getCreditCards();
          isSubmit.value = false;
          showPaymentsModal.value = false;
        } else {
          errorMessage.value =
            r.data?.msg || "Failed to add card. Please try again.";
        }
      });
    } else {
      selectedCreditCard.value = {
        new: true,
        cc_name: cardDetails.name,
        cc_number: cardDetails.cardNumber,
        cc_exp: cardDetails.expMonth + cardDetails.expYear,
        cvv: cardDetails.cvv,
      };
      showPaymentsModal.value = false;
    }
  }
};

const checkValue = () => {
  cardDetails.nickname = cardDetails.nickname.replace(/[^a-zA-Z0-9 ]/g, "");
};

const addInvoices = (list) => {
  list.forEach((inv) => {
    if (!selectedInvoices.value.some((s) => s.id === inv.id)) {
      selectedInvoices.value.push(inv);
    }
  });
};

const isProcessingPayment = ref(false);

const processPayment = () => {
  isProcessingPayment.value = true;
  const data = {
    invoices: [],
    card_details: {},
  };
  if (selectedInvoices.value.length > 0) {
    selectedInvoices.value.forEach((inv) => {
      data.invoices.push({
        invoice_id: inv.id,
        payable_amount: inv.payable_amount,
      });
    });
  }

  if (selectedCreditCard.value.new) {
    data.card_details = {
      name: selectedCreditCard.value.cc_name,
      ccnumber: selectedCreditCard.value.cc_number,
      ccexp: selectedCreditCard.value.cc_exp,
      cvv: selectedCreditCard.value.cvv,
      new: true,
    };
  } else {
    data.card_details = {
      customer_vault_id: selectedCreditCard.value.customer_vault_id,
    };
    if (cardDetails.cvv) {
      data.card_details.cvv = cardDetails.cvv;
    }
  }

  axios.api
    .post("/payments", data)
    .then((r) => {
      if (r.data?.status) {
        store.dispatch("setToast", {
          status: true,
          msg: r.data?.data?.msg || "Payment processed successfully",
          type: "success",
        });
        // getInvoices();
        const selectedIds = new Set(
          selectedInvoices.value.map((inv) => inv.id),
        );
        [
          ...invoices.value.dueInvoices,
          ...invoices.value.openInvoicesNotDue,
          ...invoices.value.allInvoices,
        ].forEach((inv) => {
          if (selectedIds.has(inv.id)) {
            inv.paid = true;
          }
        });

        selectedInvoices.value = [];
        selectedCreditCard.value = null;
        payinvoices.value = false;
        selectedPayment.value = null;
      } else {
        let msg = r.data?.msg || "Payment failed. Please try again.";
        if (r.data?.error?.includes("Card validation failed")) {
          msg =
            "Card validation failed. Please check your card details and try again.";
        }

        if (r.data?.error?.includes("Payment failed")) {
          msg = "Payment failed for some invoices";

          // if payment for some invoices failed
          const failedInvoiceIds = r.data?.failed_invoices || [];
          markInvoicesByResult(
            selectedInvoices.value,
            failedInvoiceIds,
            invoices.value,
          );
          selectedInvoices.value = [];
          selectedCreditCard.value = null;
          payinvoices.value = false;
          selectedPayment.value = null;
        }
        store.dispatch("setToast", {
          status: true,
          msg: msg,
          type: "danger",
        });
      }
    })
    .finally(() => {
      isProcessingPayment.value = false;
    });
};

const markInvoicesByResult = (selectedInvoices, failedInvoiceIds, invoices) => {
  const failedIds = new Set(failedInvoiceIds || []);
  const selectedIds = new Set(selectedInvoices.map((i) => i.id));

  // All selected that are NOT in failed are considered paid
  const paidIds = new Set([...selectedIds].filter((id) => !failedIds.has(id)));

  const all = [
    ...invoices.dueInvoices,
    ...invoices.openInvoicesNotDue,
    ...invoices.allInvoices,
  ];

  all.forEach((inv) => {
    if (failedIds.has(inv.id)) {
      inv.failed = true;
    } else if (paidIds.has(inv.id)) {
      inv.paid = true;
    }
  });

  return { failedIds, paidIds };
};

// ── Payment method tabs ───────────────────────────────────────────────────────
const activePaymentMethod = ref('creditCard'); // 'creditCard' | 'zelle' | 'wire' | 'check'

const paymentMethods = [
  { key: 'creditCard', label: 'Credit Card' },
  { key: 'zelle', label: 'Zelle' },
  { key: 'wire', label: 'Wire' },
  { key: 'check', label: 'Check' },
];

// Terms customers can't pay by card — hide the Credit Card tab for them
const isTermsCustomer = computed(() => !!invoices.value?.is_terms_customer);

const visiblePaymentMethods = computed(() =>
  isTermsCustomer.value
    ? paymentMethods.filter((m) => m.key !== 'creditCard')
    : paymentMethods,
);

watch(
  isTermsCustomer,
  (isTerms) => {
    if (isTerms && activePaymentMethod.value === 'creditCard') {
      activePaymentMethod.value = 'zelle';
    }
  },
  { immediate: true },
);

const copiedKey = ref('');

const copyToClipboard = (text, key) => {
  navigator.clipboard.writeText(text).catch(() => { });
  copiedKey.value = key;
  setTimeout(() => { copiedKey.value = ''; }, 2000);
};

const zelleReference = computed(() => {
  if (!selectedInvoices.value.length) return '';
  const date = moment().format('YYYYMMDD');
  const nums = selectedInvoices.value.map(inv => inv.invoice_number).join('-');
  return `REF-${date}-${nums}`.slice(0, 40);
});

// Payment details from API
const route = useRoute();

const wireDetails = ref({});
const zelleEmail = ref('');
const invoiceEmail = ref('');
const checkPayTo = ref('');
const checkMailTo = ref('');

const fetchBankDetails = () => {
  axios.api
    .get('/wire-details', { params: { company_id: route.params.company_id } })
    .then((res) => {
      const details = res.data?.data || {};

      wireDetails.value = {
        bankName: details.bank_name || '',
        bankAddress: details.bank_address || '',
        routing: details.routing_number || '',
        account: details.account_number || '',
        accountName: details.account_name || '',
        swiftCode: details.swift_code || '',
        businessAddress: details.business_address || '',
      };
      zelleEmail.value = details.zelle_email || '';
      invoiceEmail.value = details.invoice_email || '';
      checkPayTo.value = details.check_pay_to || '';
      checkMailTo.value = details.check_mail_to || '';
    })
    .catch((error) => {
      console.error('Failed to load bank details', error);
    });
};

fetchBankDetails();
</script>
<template>
  <Toast />
  <div class="w-full grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div class="lg:col-span-2 space-y-6">
      <div class="flex flex-col gap-6 bg-white rounded-md p-5 shadow-box flex-grow pb-6">
        <div class="flex flex-col">
          <h2 class="text-theme-secondary font-medium text-lg">
            Invoice Payments
          </h2>
          <p class="text-theme-body text-sm font-normal">
            Securely manage and pay your invoices with ease
          </p>
        </div>
        <div class="w-full grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-8 mt-2">
          <div
            class="p-3 sm:p-0 rounded-lg transition-colors border border-theme-border border-opacity-20 sm:border-transparent bg-theme-bg-shade bg-opacity-10 sm:bg-transparent hover:bg-gray-50 sm:hover:bg-transparent"
            @click="addInvoices(invoices.dueInvoices)" style="cursor: pointer;">
            <div class="flex items-center gap-2 sm:gap-3 w-full">
              <div class="flex items-center justify-center rounded-full w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0"
                style="background-color: #F8E7E7; color: #B05C5C;">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  class="w-4 h-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </div>
              <div class="sm:flex-1 sm:min-w-0">
                <div class="flex items-center gap-2 sm:block">
                  <h3 class="text-theme-secondary font-semibold text-base leading-none sm:text-lg">
                    {{ dueInvoicesCount }}
                  </h3>
                </div>
                <p class="text-theme-body text-xs sm:text-sm font-medium leading-tight mt-2 sm:mt-0">
                  Invoices Overdue
                </p>
              </div>
            </div>
          </div>

          <div
            class="p-3 sm:p-0 rounded-lg transition-colors border border-theme-border border-opacity-20 sm:border-transparent bg-theme-bg-shade bg-opacity-10 sm:bg-transparent hover:bg-gray-50 sm:hover:bg-transparent"
            @click="
              addInvoices([
                ...invoices.openInvoicesNotDue,
                ...invoices.dueInvoices,
              ])
              " style="cursor: pointer;">
            <div class="flex items-center gap-2 sm:gap-3 w-full">
              <div class="flex items-center justify-center rounded-full w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0"
                style="background-color: #FDF4DF; color: #C0AA71;">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  class="w-4 h-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </div>
              <div class="sm:flex-1 sm:min-w-0">
                <div class="flex items-center gap-2 sm:block">
                  <h3 class="text-theme-secondary font-semibold text-base leading-none sm:text-lg">
                    {{ openInvoicesCount }}
                  </h3>
                </div>
                <p class="text-theme-body text-xs sm:text-sm font-medium leading-tight mt-2 sm:mt-0">
                  Open Invoices
                </p>
              </div>
            </div>
          </div>

          <div
            class="col-span-2 sm:col-span-1 flex items-center p-3 sm:p-0 border border-theme-border border-opacity-20 sm:border-transparent rounded-lg bg-theme-bg-shade bg-opacity-10 sm:bg-transparent">
            <div class="flex items-center justify-center rounded-full w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0"
              style="background-color: #F1E0D1; color: #B08A68;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                class="w-4 h-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div class="w-full pl-3 sm:pl-4 min-w-0">
              <div class="flex items-center justify-between sm:hidden">
                <p class="text-theme-body text-xs font-medium leading-tight">Total Overdue</p>
                <h3 class="text-theme-secondary font-semibold text-sm leading-none whitespace-nowrap">
                  {{ currencyFormat(overdueAmount) }}
                </h3>
              </div>
              <div class="hidden sm:block">
                <h3 class="text-theme-secondary font-semibold text-lg leading-none whitespace-nowrap">
                  {{ currencyFormat(overdueAmount) }}
                </h3>
                <p class="text-theme-body text-sm font-medium leading-tight mt-1">Total Overdue</p>
              </div>
            </div>
          </div>

          <div
            class="col-span-2 sm:col-span-1 flex items-center p-3 sm:p-0 border border-theme-border border-opacity-20 sm:border-transparent rounded-lg bg-theme-bg-shade bg-opacity-10 sm:bg-transparent">
            <div class="flex items-center justify-center rounded-full w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0"
              style="background-color: #F1E0D1; color: #B08A68;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                class="w-4 h-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div class="w-full pl-3 sm:pl-4 min-w-0">
              <div class="flex items-center justify-between sm:hidden">
                <p class="text-theme-body text-xs font-medium leading-tight">Total Outstanding</p>
                <h3 class="text-theme-secondary font-semibold text-sm leading-none whitespace-nowrap">
                  {{ currencyFormat(outstandingAmount) }}
                </h3>
              </div>
              <div class="hidden sm:block">
                <h3 class="text-theme-secondary font-semibold text-lg leading-none whitespace-nowrap">
                  {{ currencyFormat(outstandingAmount) }}
                </h3>
                <p class="text-theme-body text-sm font-medium leading-tight mt-1">Total Outstanding</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Block :title="isInvoicesLoading
        ? 'Unpaid Invoices'
        : `Unpaid Invoices (${allUnpaidInvoices.length})`
        " header-class="invoices-header" v-if="isInvoicesLoading || allUnpaidInvoices.length > 0">
        <template #action>
          <div class="md:h-5 flex items-center md:justify-end gap-2 w-full md:w-auto">
            <button v-if="selectedInvoices.length && !isInvoicesLoading" @click="downloadInvoices" :disabled="downloading"
              class="px-3 py-1.5 m-0 border-0 bg-brand-indigo bg-opacity-50 rounded-full text-brand-indigodarkest font-normal text-xs inline-flex flex-shrink-0 items-center justify-start leading-none gap-1">
              <Icon name="download" class="w-3 h-3" />
              <span><em class="hidden sm:inline-block not-italic">Download</em>
                Invoices({{ selectedInvoices.length }})
              </span>
            </button>
          </div>
        </template>
        <div class="overflow-x-auto -mx-6 -mb-6 -mt-6">
          <table class="min-w-full divide-y divide-theme-border divide-opacity-40">
            <thead class="hidden md:table-header-group bg-theme-bg-shade bg-opacity-10">
              <tr v-show="!isInvoicesLoading">
                <th scope="col" class="py-3.5 pl-5 pr-3 text-left text-sm font-semibold text-theme-secondary sm:pl-6">
                  <div class="checkbox">
                    <input type="checkbox" :checked="allSelected" @change="toggleAllInvoices" />
                  </div>
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-theme-secondary">
                  Invoice #
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-theme-secondary">
                  Order #
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-theme-secondary">
                  Date
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-theme-secondary">
                  Due Date
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-theme-secondary">
                  Amount
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-theme-secondary">
                  Status
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-theme-border divide-opacity-30 bg-white">
              <template v-if="isInvoicesLoading">
                <template v-for="i in 5" :key="`skeleton-${i}`">
                  <tr class="hidden md:table-row">
                    <td class="whitespace-nowrap py-4 pl-5 pr-3 sm:pl-6">
                      <div class="w-4 h-4 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4">
                      <div class="w-20 h-4 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4">
                      <div class="w-24 h-4 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4">
                      <div class="w-24 h-4 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4">
                      <div class="w-24 h-4 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4">
                      <div class="w-16 h-4 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td class="whitespace-nowrap px-0 py-4">
                      <div class="w-20 h-5 bg-gray-200 animate-pulse rounded-full"></div>
                    </td>
                  </tr>

                  <tr class="md:hidden">
                    <td colspan="7" class="px-4 py-2">
                      <div class="space-y-2 animate-pulse">
                        <div class="flex items-start justify-between gap-2">
                          <div class="w-4 h-4 bg-gray-200 rounded mt-1"></div>
                          <div class="flex-1 min-w-0 space-y-2">
                            <div class="flex items-center gap-2">
                              <div class="h-3.5 w-24 bg-gray-200 rounded"></div>
                              <div class="h-4 w-12 bg-gray-200 rounded-full"></div>
                            </div>
                            <div class="h-3 w-32 bg-gray-200 rounded"></div>
                          </div>
                          <div class="h-4 w-14 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
              <template v-else>
                <InvoiceRow :allUnpaidInvoices="allUnpaidInvoices" :toggleInvoice="toggleInvoice"
                  :isSelected="isSelected" :formatDate="formatDate" :moment="moment" />
              </template>
            </tbody>
          </table>
        </div>
      </Block>
      <div v-else-if="!isInvoicesLoading"
        class="flex flex-col items-center justify-center p-12 bg-white rounded-md shadow-box">
        <Icon name="document-file" class="w-16 h-16 text-theme-border mb-4" />
        <p class="text-theme-body text-lg font-medium">
          No unpaid invoices found
        </p>
      </div>
    </div>
    <div class="lg:col-span-1">
      <div class="sticky top-20 flex flex-col gap-6">
        <div class="bg-white rounded-md shadow-box">
          <div class="px-5 pt-5 pb-0">
            <h3 class="text-theme-secondary font-semibold text-sm">Payment Instructions</h3>
            <p class="text-theme-body text-xs mt-0.5">
              {{ selectedInvoices.length
                ? 'Choose a payment method below.'
                : 'Select invoices to the left to calculate your total and reference. Bank details are always available below.' }}
            </p>
          </div>

          <!-- Method tabs -->
          <div class="flex border-b border-theme-border border-opacity-30 mt-4">
            <button v-for="m in visiblePaymentMethods" :key="m.key" @click="activePaymentMethod = m.key"
              class="flex-1 flex flex-col items-center gap-0.5 py-2.5 px-2 text-tiny font-medium border-b-2 -mb-px"
              :class="activePaymentMethod === m.key
                ? 'border-theme-secondary text-theme-secondary bg-theme-bg-shade bg-opacity-20'
                : 'border-transparent text-theme-body hover:text-theme-secondary hover:bg-theme-bg-shade hover:bg-opacity-10'">
              <template v-if="m.key === 'creditCard'">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </template>
              <template v-else-if="m.key === 'zelle'">
                <img src="/images/card-providers/zelle.png" alt="Zelle" class="w-3.5 h-3.5 object-contain" />
              </template>
              <template v-else-if="m.key === 'wire'">
                <img src="/images/card-providers/bank.jpg" alt="Wire" class="w-3.5 h-3.5 object-contain" />
              </template>
              <template v-else-if="m.key === 'check'">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="6" width="20" height="14" rx="2" />
                  <line x1="5" y1="11" x2="13" y2="11" />
                  <rect x="15" y="9" width="5" height="4" rx="1" />
                  <line x1="5" y1="16" x2="10" y2="16" />
                </svg>
              </template>
              <span>{{ m.label }}</span>
            </button>
          </div>

          <!-- Tab content -->
          <div class="p-5">

          <!-- ── Credit Card tab ── -->
          <div v-if="activePaymentMethod === 'creditCard' && !isTermsCustomer">

          <!-- Selected Invoices Section -->
          <div class="space-y-3" v-if="selectedInvoices.length > 0">
            <h4 class="text-xs font-medium text-theme-secondary">
              Selected Invoices ({{ selectedInvoices.length }})
            </h4>
            <div class="flex flex-col px-3 invoices-list">
              <div v-for="inv in selectedInvoices" :key="inv.id"
                class="flex flex-col gap-2 border-b border-theme-bg-shade border-opacity-50 py-2 invoices-list-item">
                <div class="flex items-center justify-between w-full gap-2">
                  <div class="flex flex-col gap-0.5">
                    <p class="text-sm text-theme-secondary font-medium">
                      Invoice:&nbsp;<span class="uppercase">{{
                        inv.invoice_number
                        }}</span>
                    </p>
                    <span class="text-xs text-theme-body font-medium">{{ inv.order_number }} -
                      {{ formatDate(inv.due_at) }}</span>
                  </div>
                  <p class="text-base text-theme-secondary font-medium">
                    ${{ currencyFormat(inv.payable_amount) }}
                  </p>
                </div>
              </div>
            </div>

            <div class="pt-4 border-t border-theme-border border-opacity-30 space-y-4">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-medium text-theme-secondary">
                  Payment Details
                </h4>
                <button v-if="selectedCreditCard && invoices.cards?.length > 1"
                  class="text-xs font-semibold text-brand-primary hover:underline uppercase"
                  @click="selectedCreditCard = selectedPayment = null">
                  Change Card
                </button>
              </div>

              <!-- Card Selection or Selected Card -->
              <div v-if="!selectedCreditCard" class="space-y-3">
                <div class="max-h-60 overflow-y-auto pr-1 -mr-1 cards-list">
                  <RadioGroup v-model="selectedPayment" class="grid grid-cols-1 gap-2.5">
                    <RadioGroupOption as="template" v-for="card in invoices.cards" :key="card.id" :value="card"
                      v-slot="{ checked }" @click="selectCreditCard(card)">
                      <div
                        class="relative flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-all duration-200"
                        :class="[
                          checked
                            ? 'border-theme-indigo bg-indigo-50 bg-opacity-30'
                            : 'border-theme-border  hover:border-theme-indigo hover:bg-indigo-50 hover:bg-opacity-30',
                        ]">
                        <div
                          class="flex-shrink-0 w-8 h-6 flex items-center justify-center bg-white rounded border border-gray-100 p-0.5">
                          <img :src="card.cc_number[0] == 4
                            ? '/images/card-providers/visa.png'
                            : card.cc_number[0] == 5
                              ? '/images/card-providers/master-card.png'
                              : card.cc_number[0] == 6
                                ? '/images/card-providers/Discover-logo.png'
                                : card.cc_number[0] == 3
                                  ? '/images/card-providers/american-express.png'
                                  : ''
                            " class="max-h-full" />
                        </div>
                        <div class="flex flex-col flex-grow min-w-0">
                          <p class="text-xs font-medium text-theme-secondary truncate m-0 leading-tight">
                            {{ "•••• " + card.cc_number.slice(-4) }}
                          </p>
                          <span class=" text-xs text-theme-body font-medium leading-none mt-1">{{ card.cc_name
                            }}</span>
                        </div>
                        <div :class="[
                          checked
                            ? 'bg-theme-indigo text-white'
                            : 'border text-transparent',
                          'rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0',
                        ]">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    </RadioGroupOption>
                  </RadioGroup>
                </div>
                <button class="card-btn" @click="openPaymentModal">
                  <Icon class="ml-0.5" name="plus" />
                  <span class="hidden sm:inline">Add New Card</span>
                </button>
              </div>

              <!-- Selected Card Display -->
              <div v-else class="cc-form pre-selected compact rounded-md border-theme-indigo border-opacity-30">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="check-mark">
                  <path fill-rule="evenodd"
                    d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                    clip-rule="evenodd" />
                </svg>
                <div class="flex items-center justify-between gap-2 h-8 card-number">
                  <div class="flex flex-col">
                    <p class="text-sm font-medium text-theme-secondary leading-none">
                      {{
                        "xxxx-xxxx-xxxx-" +
                        selectedCreditCard.cc_number.slice(-4)
                      }}
                    </p>
                    <p class="text-tiny font-medium text-theme-body opacity-60 mt-1">
                      Expiry: {{ selectedCreditCard.cc_exp.slice(0, 2) }}/{{
                        selectedCreditCard.cc_exp.slice(-2)
                      }}
                    </p>
                  </div>
                  <img :src="selectedCreditCard.cc_number[0] == 4
                    ? '/images/card-providers/visa.png'
                    : selectedCreditCard.cc_number[0] == 5
                      ? '/images/card-providers/master-card.png'
                      : selectedCreditCard.cc_number[0] == 6
                        ? '/images/card-providers/Discover-logo.png'
                        : selectedCreditCard.cc_number[0] == 3
                          ? '/images/card-providers/american-express.png'
                          : ''
                    " class="h-4" />
                </div>
                <div class="flex items-center gap-2 mt-2 pt-2 border-t border-theme-border border-opacity-10"
                  v-if="!isAuthenticated && !selectedCreditCard.new">
                  <span class="text-[10px] font-bold uppercase opacity-60">Security Code:</span>
                  <input v-model="cardDetails.cvv" v-maska="getCvvMaskForCard(selectedCreditCard.cc_number)"
                    placeholder="•••" class="cvv-input ml-auto" />
                </div>
              </div>
            </div>

            <div class="pt-6 border-t border-theme-border border-opacity-30 space-y-4">
              <div class="flex items-center justify-between">
                <h3 class="text-base font-medium text-theme-secondary">
                  Total Amount:
                </h3>
                <p class="text-xl font-medium text-theme-secondary">
                  ${{ currencyFormat(totalAmount) }}
                </p>
              </div>

              <Button title="Pay Now" class="w-full py-4 text-sm font-bold shadow-sm transition-all duration-200"
                :class="[
                  !isPaymentValid
                    ? 'bg-gray-800 opacity-50'
                    : 'bg-gray-900 hover:bg-black',
                ]" @click="processPayment" :disabled="isProcessingPayment || !isPaymentValid" />
            </div>
          </div>

          <!-- No invoices selected -->
          <div v-else class="flex flex-col items-center text-center py-8 space-y-3">
            <div class="w-12 h-12 rounded-full bg-theme-bg-shade bg-opacity-30 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-theme-body" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium text-theme-secondary">Select invoices to the left to pay by card</p>
              <p class="text-xs text-theme-body mt-1.5">Choose your invoices, then complete payment here — no bank
                transfer needed.</p>
            </div>
          </div>
          </div>

          <!-- ── Zelle tab ── -->
          <ZelleInstructions v-else-if="activePaymentMethod === 'zelle'" :zelle-email="zelleEmail"
            :zelle-reference="zelleReference" :total-amount="totalAmount"
            :selected-invoices-count="selectedInvoices.length" :copied-key="copiedKey"
            :copy-to-clipboard="copyToClipboard" :currency-format="currencyFormat" layout="sidebar" />

          <!-- ── Wire tab ── -->
          <WireInstructions v-else-if="activePaymentMethod === 'wire'" :wire-details="wireDetails"
            :zelle-reference="zelleReference" :total-amount="totalAmount"
            :selected-invoices-count="selectedInvoices.length" :copied-key="copiedKey"
            :copy-to-clipboard="copyToClipboard" :currency-format="currencyFormat" layout="sidebar" />

          <!-- ── Check tab ── -->
          <CheckInstructions v-else-if="activePaymentMethod === 'check'" :check-pay-to="checkPayTo"
            :check-mail-to="checkMailTo" :invoice-email="invoiceEmail" :zelle-reference="zelleReference"
            :total-amount="totalAmount" :copied-key="copiedKey" :copy-to-clipboard="copyToClipboard"
            :currency-format="currencyFormat" layout="sidebar" />

          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Credit Card Modal -->
  <ModalFull :is-visible="showPaymentsModal" :reset-container="true" @close="showPaymentsModal = isSubmit = false"
    container-class="w-full max-w-2xl">
    <div class="w-full bg-white">
      <div class="flex items-center justify-between bg-gray-100 px-5 py-3 sm:p-5">
        <h3 class="flex items-center text-base sm:text-lg font-medium">
          <Icon name="add-card" class="w-6 h-6 mr-2" />
          <span>Add New Card</span>
        </h3>
        <button @click="showPaymentsModal = isSubmit = false"
          class="flex items-center uppercase font-medium text-xs cursor-pointer text-gray-400">
          <Icon name="close" class="w-5 h-5 mr-1" />
        </button>
      </div>
      <div class="grid grid-cols-5 w-full divide-x divide-gray-100">
        <div class="flex-grow col-span-5 md:col-span-3">
          <div class="flex flex-col items-start text-left space-y-4 p-5">
            <div class="w-full space-y-1" v-if="isAuthenticated">
              <div class="flex items-center w-full space-x-2">
                <label for="" class="text-xs text-theme-body leading-none">Card Nickname (Optional)</label>
                <tippy interactive>
                  <span class="cursor-pointer">
                    <Icon name="info" class="w-4 h-4 -mt-0.5 text-theme-secondary opacity-70" />
                  </span>
                  <template #content>
                    <p class="text-left px-1.5 text-xs">
                      A nickname helps you remember what a specific credit card
                      is for. For example: Main Street Store Card, Jones Street
                      Store Card.
                    </p>
                  </template>
                </tippy>
              </div>

              <div class="relative">
                <input type="text" v-model="cardDetails.nickname" name="" @keyup="checkValue()" id="" maxlength="32"
                  class="placeholder:font-light placeholder:text-gray-400  text-md sm:text-base w-full border border-theme-body border-opacity-50 rounded focus:outline-none pl-3 pr-6 py-2 focus:border-theme-body focus:ring-transparent" />
              </div>
            </div>

            <div class="w-full space-y-1">
              <label for="" class="text-xs text-theme-body leading-none">Card Number</label>
              <div class="relative">
                <input type="text" v-model="cardDetails.cardNumber" v-maska="getCardMask" name="" id=""
                  class="placeholder:font-light placeholder:text-gray-400  text-md sm:text-base w-full border border-theme-body border-opacity-50 rounded focus:outline-none pl-3 pr-6 py-2 focus:border-theme-body focus:ring-transparent" />
                <span class="pointer-events-none h-4 absolute right-2 top-1/2 -mt-2 w-8">
                  <img :src="cardDetails.cardNumber[0] == 4
                    ? '/images/card-providers/visa.png'
                    : cardDetails.cardNumber[0] == 5
                      ? '/images/card-providers/master-card.png'
                      : cardDetails.cardNumber[0] == 6
                        ? '/images/card-providers/Discover-logo.png'
                        : cardDetails.cardNumber[0] == 3
                          ? '/images/card-providers/american-express.png'
                          : null
                    " class="max-w-full" />
                </span>
              </div>
              <span v-if="
                isSubmit && v$.cardDetails.cardNumber.$silentErrors.length > 0
              " class="text-theme-red font-semibold text-xs">
                <span v-if="
                  v$.cardDetails.cardNumber.$silentErrors[0].$validator ==
                  'required'
                ">Required</span>
                <span v-else-if="
                  v$.cardDetails.cardNumber.$silentErrors[0].$validator ==
                  'numeric'
                ">Only Number</span>
                <span v-else-if="
                  v$.cardDetails.cardNumber.$silentErrors[0].$validator ==
                  'maxLength'
                ">Only 16 digits</span>
                <span v-else-if="
                  v$.cardDetails.cardNumber.$silentErrors[0].$validator ==
                  'minLength'
                ">Only 15 digits</span>
              </span>
            </div>

            <div class="w-full space-y-1">
              <label for="" class="text-xs text-theme-body leading-none">Name on card</label>
              <div class="relative">
                <input type="text" v-model="cardDetails.name" name="" id=""
                  class="placeholder:font-light placeholder:text-gray-400  text-md sm:text-base w-full border border-theme-body border-opacity-50 rounded focus:outline-none pl-3 pr-6 py-2 focus:border-theme-body focus:ring-transparent" />
              </div>
              <span v-if="isSubmit && v$.cardDetails.name.$silentErrors.length > 0"
                class="text-theme-red font-semibold text-xs">
                <span v-if="
                  v$.cardDetails.name.$silentErrors[0].$validator ==
                  'required'
                ">Required</span>
              </span>
            </div>
            <!-- </div> -->
            <div class="grid grid-cols-3 gap-5">
              <div class="w-full space-y-1">
                <label for="" class="text-xs text-theme-body leading-none">Exp<span
                    class="hidden sm:inline">iration</span> Month</label>
                <div class="relative">
                  <select name="expMonth" id="expMonth"
                    class="text-base w-full border border-theme-body border-opacity-50 rounded focus:outline-none pl-3 pr-6 py-2 focus:border-theme-body focus:border-opacity-80 focus:ring-transparent"
                    v-model="cardDetails.expMonth">
                    <option :value="index" v-for="(val, index) in expMonths" :key="index">{{ val }}</option>
                  </select>
                </div>
                <span v-if="
                  isSubmit && v$.cardDetails.expMonth.$silentErrors.length > 0
                " class="text-theme-red font-semibold text-xs">
                  <span v-if="
                    v$.cardDetails.expMonth.$silentErrors[0].$validator ==
                    'required'
                  ">Required</span>
                  <span v-else-if="
                    v$.cardDetails.expMonth.$silentErrors[0].$validator ==
                    'minValue'
                  ">Current Month</span>
                </span>
              </div>

              <div class="w-full space-y-1">
                <label for="" class="text-xs text-theme-body leading-none">Exp<span
                    class="hidden sm:inline">iration</span> Year</label>
                <div class="relative">
                  <select name="expYear" id="expYear"
                    class="text-base w-full border border-theme-body border-opacity-50 rounded focus:outline-none pl-3 pr-6 py-2 focus:border-theme-body focus:border-opacity-80 focus:ring-transparent"
                    v-model="cardDetails.expYear">
                    <option :value="exp" v-for="exp in expYears" :key="exp">{{
                      exp
                      }}</option>
                  </select>
                </div>
                <span v-if="
                  isSubmit && v$.cardDetails.expYear.$silentErrors.length > 0
                " class="text-theme-red font-semibold text-xs">
                  <span v-if="
                    v$.cardDetails.expYear.$silentErrors[0].$validator ==
                    'required'
                  ">Required</span>
                </span>
              </div>

              <div class="w-full space-y-1">
                <label for="" class="text-xs text-theme-body leading-none">CVV</label>
                <div class="relative">
                  <input type="text" v-maska="getCvvMask" v-model="cardDetails.cvv" name="" id=""
                    class="placeholder:font-light placeholder:text-gray-400  text-md sm:text-base w-full border border-theme-body border-opacity-50 rounded focus:outline-none pl-3 pr-6 py-2 focus:border-theme-body focus:ring-transparent" />
                </div>
                <span v-if="isSubmit && v$.cardDetails.cvv.$silentErrors.length > 0"
                  class="text-theme-red font-semibold text-xs">
                  <span v-if="
                    v$.cardDetails.cvv.$silentErrors[0].$validator ==
                    'required'
                  ">Required</span>
                  <span v-else-if="
                    v$.cardDetails.cvv.$silentErrors[0].$validator ==
                    'numeric'
                  ">Only Number</span>
                  <span v-else-if="
                    v$.cardDetails.cvv.$silentErrors[0].$validator ==
                    'maxLength'
                  ">Only 03 digits</span>
                </span>
              </div>
            </div>

            <div
              class="flex items-center space-x-3 bg-theme-bg-shade bg-opacity-20 px-3 py-2 rounded text-sm text-theme-body w-full">
              <Icon name="lock" class="w-4 h-4" />
              <span>We never store your credit card information</span>
            </div>

            <p class="text-theme-red font-semibold text-xs" v-if="errorMessage && isSubmit">
              {{ errorMessage }}
            </p>
          </div>
          <div @click="addNewPaymentCard" class="flex items-center justify-between px-5 pt-3 pb-6">
            <Button icon="right-arrow" title="Add Card" />
          </div>
        </div>
        <div class="hidden md:flex flex-col space-y-5 col-span-2 py-4 px-6 text-left text-sm">
          <Icon name="user-sheild" class="w-12 h-12" />
          <h4 class="text-base font-semibold">Your information is safe</h4>
          <div class="space-y-3 text-theme-body-light">
            <p>
              We do not store your credit card information on our servers.
            </p>
            <p>
              Credit Card information is passed via a secure socket layer to our
              payment processor where your information is tokenized (a random
              number is generated to represent your card).
            </p>
            <p>
              No employee of Medusa has access to your complete credit card
              number.
            </p>
          </div>
        </div>
      </div>
    </div>
  </ModalFull>
</template>

<style lang="scss" scoped>
.checkbox {
  @apply flex items-center space-x-3 select-none cursor-pointer;

  &.align-start {
    @apply items-start;
  }

  input {
    @apply h-4 w-4 border-2 border-brand-secondary border-opacity-50 rounded text-brand-secondary focus:ring-transparent cursor-pointer;

    &:focus-visible {
      @apply border-brand-secondary ring-2 ring-brand-gray;
    }
  }

  span {
    @apply font-medium text-base text-theme-body leading-none;

    &.xs {
      @apply text-sm font-normal;
    }
  }
}

.invoices-list {
  max-height: calc(55 * 3px);
  overflow-y: auto;
  @include CssScrollbar(5px, 0.2);
  @apply bg-theme-bg-shade bg-opacity-25 rounded-md;
}

.cards-list {
  max-height: calc(69 * 3px);
  overflow-y: auto;
  @include CssScrollbar(5px, 0.2);
}

.invoices-list-item {
  &:last-child {
    @apply border-0;
  }
}

.cc-form {
  @apply flex flex-col gap-0 items-stretch;

  &.pre-selected {
    @apply relative gap-1 border border-theme-indigo border-opacity-50 rounded-md bg-theme-bg-shade bg-opacity-10 px-3 py-2 pl-8;

    .check-mark {
      @apply absolute left-2 top-2 text-theme-indigo w-5 h-5;
    }

    p,
    span {
      @apply text-sm text-theme-dark-shade leading-none m-0;
    }

    .cvv-input {
      @apply rounded-md text-sm leading-none px-2 py-1.5 w-16 mb-0;
    }

    &.compact {
      @apply py-1.5 px-3 pl-7;

      .check-mark {
        @apply left-1.5 top-2 w-4 h-4;
      }

      p {
        @apply text-xs leading-snug;
      }
    }
  }

  .card-number {
    @apply w-full;

    input {
      margin-bottom: -1px;
      @apply rounded-t-md pr-12;
    }
  }

  input {
    @apply border border-theme-border transition-all duration-300 border-opacity-60 outline-none focus:outline-none w-full px-2 py-3 leading-none text-sm placeholder-theme-light-shade;

    &:focus-visible {
      @apply z-10 border-theme-indigo;
    }
  }

  .expiry-input {
    @apply rounded-bl-md;
  }

  .cvv-input {
    @apply rounded-br-md;
    margin-left: -1px;
  }
}

.checkblock {
  @apply h-full relative flex flex-col rounded-lg border border-theme-border bg-white shadow-sm px-6 py-4 cursor-pointer hover:border-gray-400 focus:outline-none select-none;

  &.disabled {
    @apply bg-theme-bg hover:bg-theme-bg border-theme-border hover:border-theme-border cursor-default pointer-events-none;
  }

  &.min-height {
    min-height: 175px;
  }

  .checkmark-label {
    @apply flex w-full justify-end -mb-6;

    &.narrow-margin {
      @apply -mb-2;
    }

    .checkmark {
      @apply border rounded-full w-5 h-5 flex flex-shrink-0 justify-center items-center;

      &.active {
        @apply bg-theme-indigo border-theme-indigo text-white;
      }

      &.inactive {
        @apply bg-white border-theme-border;
      }
    }
  }

  .go-back {
    @apply mb-4 bg-gray-100 p-3 justify-between;
    width: fit-content;
    border-radius: 100%;
  }

  &.back-block {
    .item-desc {
      @apply m-0 w-full;
    }
  }

  .item-desc {
    @apply block text-sm text-theme-secondary font-normal text-opacity-75 w-11/12 mb-3;
  }

  .item-nickname {
    @apply text-xs font-normal text-theme-body mb-1;
  }

  &.narrow-padding {
    @apply px-3 py-1.5;
  }
}

.card-btn {
  @apply rounded-md px-2 py-1.5 leading-none font-medium text-white bg-theme-secondary inline-flex items-center uppercase tracking-wider;
  font-size: 10px;

  svg {
    @apply relative w-4 h-4 -mt-0.5 mr-1;
    bottom: -1px;
  }

  span {
    @apply relative;
    bottom: -1px;
  }
}
</style>

<style>
body #app[data-page="payments"] {
  overflow: visible !important;
}

.invoices-header {
  flex-direction: row !important;
}
</style>
